import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Divider, Grid, Stack } from '@mui/material';
import moment from 'moment';

import { MasterNameApi } from '@/api';
import { DriverLicenseApi } from '@/api/api.driver.license';
import { FormCard, FormRoot, RenderFormField } from '@/components';
import { Gender } from '@/constants/form_inputs';
import {
  PageBackHeader,
  TabAnimationWrapper,
  useTabLayoutContext,
} from '@/layouts/TabLayout';
import { IMasterNamePerson } from '@/models';
import {
  IDriverLicense,
  IDriverLicenseSaveRequest,
} from '@/models/driver.license';
import { ConfirmTypes, useToastStore } from '@/store/store.toast';
import colors from '@/theme/variables/colors';
import { heightExactToDecimalFeet } from '@/utils/common';

import { useMasterNamesContext } from '..';
import {
  DriverLicenseFormFields,
  TabNamesNextPaths,
  TabNamesScreens,
} from '../data';
import { UserAvatar } from '../styles';

export const MasterAddDriverLicense = () => {
  const { updateCurrentScreen } = useTabLayoutContext();
  const { updateToast } = useToastStore();
  const {
    currentDriverLicenseId,
    handleUpdateMasterName,
    currentMastername,
    setTemporaryDriverLicense,
    temporaryDriverLicense,
  } = useMasterNamesContext();

  const methods = useForm<IDriverLicense>({
    mode: 'all',
  });

  const { handleSubmit, getValues, register, reset } = methods;

  useEffect(() => {
    if (currentDriverLicenseId) {
      fetchCurrentDriverLicense();
    } else if (temporaryDriverLicense) {
      // Load temporary driver license data if available
      reset(temporaryDriverLicense);
    }
  }, [currentDriverLicenseId, temporaryDriverLicense]);

  const fetchCurrentDriverLicense = async () => {
    try {
      const res = await DriverLicenseApi.getDriverLicense(
        String(currentDriverLicenseId),
      );
      methods.reset({
        ...res.data,
      });
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const onSubmit = async (values: IDriverLicense) => {
    try {
      // Always mark as temporary if person ID doesn't exist yet
      if (!currentMastername?.masterName?._id) {
        // Store in context for later use
        setTemporaryDriverLicense(values as any);

        updateToast({
          open: true,
          message:
            'License information stored temporarily. Please complete and save person details first.',
          type: ConfirmTypes.SUCCESS,
        });

        // Navigate back to person details
        updateCurrentScreen(TabNamesScreens.MasterAddNameDetails);
        return;
      }

      const personId = currentMastername.masterName._id;
      const isTemporary = !!(currentMastername as any)?.isTemporary;

      // Get person data from the current master name
      const {
        firstName,
        lastName,
        middleName = '',
        birthday,
        sex: gender,
      } = currentMastername?.masterName as IMasterNamePerson;

      const { eyeColor, heightExact } = (
        currentMastername?.masterName as IMasterNamePerson
      ).physicalCharacteristics || {
        eyeColor: undefined,
        heightExact: undefined,
      };

      // Ensure we preserve the _id for updates
      const payload: IDriverLicenseSaveRequest = {
        ...values,
        _id: values._id || currentDriverLicenseId, // Ensure ID is preserved for updates
        firstName: firstName || '',
        lastName: lastName || '',
        middleName,
        gender: gender || Gender.UNKNOWN,
        birthday: birthday ? moment(birthday).format('YYYY-MM-DD') : undefined,
        issueDate: values.issueDate
          ? moment(values.issueDate).format('YYYY-MM-DD')
          : undefined,
        expireDate: values.expireDate
          ? moment(values.expireDate).format('YYYY-MM-DD')
          : undefined,
        revisionDate: values.revisionDate
          ? moment(values.revisionDate).format('YYYY-MM-DD')
          : undefined,
        eyeColor,
        height: heightExactToDecimalFeet(heightExact),
        address: values.address?._id || '',
      };

      // Check if we're working with a temporary master name record
      if (isTemporary) {
        // Store driver license data in temporary state instead of saving to API
        setTemporaryDriverLicense(payload as any);
        updateToast({
          open: true,
          message: 'License information stored temporarily',
          type: ConfirmTypes.SUCCESS,
        });
        updateCurrentScreen(TabNamesScreens.MasterAddNameDetails);
        return;
      }

      // From here on, we're dealing with a permanent record with an ID

      // Determine if this is an update or create operation
      const isUpdate = !!values._id || !!currentDriverLicenseId;

      // Save the driver's license
      const savedLicense = await DriverLicenseApi.saveDriverLicense(payload);

      if (!savedLicense?.data?._id) {
        updateToast({
          open: true,
          message: 'Failed to save driver license: No ID returned',
          type: ConfirmTypes.ERROR,
        });
        return false;
      }

      // First, get the latest person data to ensure we have the correct firstName and lastName
      try {
        const personData = await MasterNameApi.getMasterName(personId);

        if (!personData?.data) {
          throw new Error('Could not fetch person data for update');
        }

        // Only update the master name reference if this is a new license
        // or if the license ID changed
        const shouldUpdateMasterName =
          !isUpdate ||
          (currentMastername.masterName.type === 'PERSON' &&
            (currentMastername.masterName as IMasterNamePerson).driverLicense
              ?._id !== savedLicense.data._id);

        if (shouldUpdateMasterName) {
          // Create update data with minimal fields to avoid sending nulls
          const updateData = {
            firstName: personData.data.firstName,
            lastName: personData.data.lastName,
            driverLicense: savedLicense.data._id,
          };

          await handleUpdateMasterName(personId, updateData);
        }

        updateToast({
          open: true,
          message: `License ${isUpdate ? 'updated' : 'saved'}`,
          type: ConfirmTypes.SUCCESS,
        });
        updateCurrentScreen(TabNamesScreens.MasterAddNameDetails);
      } catch (fetchErr: any) {
        updateToast({
          open: true,
          message: `Failed to update person with license: ${fetchErr.message}`,
          type: ConfirmTypes.ERROR,
        });
      }
    } catch (err: any) {
      updateToast({
        open: true,
        message: `Error saving driver license: ${err.message}`,
        type: ConfirmTypes.ERROR,
      });
    }
  };

  const onGoBack = () => {
    updateCurrentScreen(TabNamesScreens.MasterAddNameDetails);
  };

  return (
    <TabAnimationWrapper nextPaths={TabNamesNextPaths.driverLicense}>
      <PageBackHeader
        title="Driver's license / ID card information"
        goBack={onGoBack}
      />
      <Stack flexDirection="row" justifyContent="left" alignItems="center">
        <UserAvatar sx={{ color: colors.error.dark, fontSize: 28 }}>
          {getValues().firstName?.charAt(0) ||
            (currentMastername?.masterName as any)?.firstName?.charAt(0) ||
            ''}
          {getValues().lastName?.charAt(0) ||
            (currentMastername?.masterName as any)?.lastName?.charAt(0) ||
            ''}
        </UserAvatar>
      </Stack>
      <Divider sx={{ mx: 3, mb: 2 }} />
      <FormProvider {...methods}>
        <FormRoot sx={{ py: 2, flex: 1 }} onSubmit={handleSubmit(onSubmit)}>
          <Grid spacing={2} mx={2} container>
            {DriverLicenseFormFields.map(({ key, items, title }) => (
              <FormCard key={key} className="grey-title" title={title}>
                {items.map(({ grid, ...rest }: any) => {
                  return rest.type === 'hidden' ? (
                    <input type="hidden" {...register(rest.name)} />
                  ) : (
                    <Grid
                      key={rest.name}
                      xs={12}
                      sm={6}
                      md={3}
                      {...grid}
                      item
                      mb={2}
                    >
                      <RenderFormField {...rest} />
                    </Grid>
                  );
                })}
              </FormCard>
            ))}
          </Grid>
          {/* Hidden field to ensure ID is submitted */}
          <input type="hidden" {...register('_id')} />
        </FormRoot>
        <Divider sx={{ my: 2, width: '100%' }} />
        <Stack
          columnGap={2}
          flexDirection="row"
          justifyContent="end"
          m={3}
          flex={1}
          flexGrow={0}
        >
          <Button
            variant="contained"
            color="inherit"
            size="medium"
            onClick={onGoBack}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            size="medium"
            type="submit"
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>
        </Stack>
      </FormProvider>
    </TabAnimationWrapper>
  );
};
